import React, { useState, useEffect, useMemo, createContext, useContext } from 'react';
import { saveAs } from 'file-saver'; // For downloading the CSS file
// To this:
import { ChevronDown, ChevronUp, Download, Palette, RefreshCw, Sparkles, Loader2, AlertTriangle, X } from 'lucide-react'; // Icons
// Tailwind CSS is assumed to be available globally (e.g., via CDN in index.html or setup in project)

// --- Theme Definitions and Logic ---

const THEME_DEFINITIONS = {
  "datacentral-default": {
    name: "DataCentral Default",
    settings: {
      fontFamily: "'Inter', sans-serif",
      sidebarBg: "#f8f9fa",
      sidebarText: "#495057",
      sidebarHoverText: "#007bff",
      sidebarActiveBg: "#007bff",
      sidebarActiveText: "white",
      sidebarBorder: "#dee2e6",
      sidebarBorderRadius: "8px",
      sidebarLinkHoverBg: "#e9ecef",
      headerBg: "#ffffff",
      headerBorder: "#dee2e6",
      headerButtonBorder: "#007bff",
      headerButtonText: "#007bff",
      headerButtonHoverBg: "#007bff",
      headerButtonHoverText: "white",
      pageBg: "#f8f9fa",
      cardBodyBg: "#ffffff",
      cardBorderRadius: "8px",
      formControlBg: "#ffffff",
      formControlText: "#495057",
      formControlBorder: "#ced4da",
      formControlPlaceholderColor: "#6c757d",
      btnPrimaryBg: "#007bff",
      btnPrimaryText: "white",
      btnPrimaryHoverBg: "#0056b3",
      btnPrimaryActiveBg: "#004085",
      btnPrimaryDisabledBg: "#6c757d",
      btnPrimaryBorderRadius: "4px",
      tableHeaderBg: "#e9ecef",
      tableHeaderText: "#495057",
      tableRowBg: "#ffffff",
      tableRowHoverBg: "#f8f9fa",
      tableRowBorder: "#dee2e6",
    },
  },
  "vibrant-orange-classic": {
    name: "Vibrant Orange Classic",
    settings: {
      fontFamily: "'Inter', sans-serif",
      sidebarBg: "#EAEAEA",
      sidebarText: "#222222",
      sidebarHoverText: "#FF6A00",
      sidebarActiveBg: "#FF8C42",
      sidebarActiveText: "white",
      sidebarBorder: "#CCCCCC",
      sidebarBorderRadius: "15px",
      sidebarLinkHoverBg: "#D9D9D9",
      headerBg: "#F4F4F4",
      headerBorder: "#CCCCCC",
      headerButtonBorder: "#FF8C42",
      headerButtonText: "#663300",
      headerButtonHoverBg: "#FF6A00",
      headerButtonHoverText: "white",
      pageBg: "#FFFFFF",
      cardBodyBg: "#F5F5F5",
      cardBorderRadius: "10px",
      formControlBg: "white",
      formControlText: "#333333",
      formControlBorder: "#CCCCCC",
      formControlPlaceholderColor: "#999999",
      btnPrimaryBg: "#FF6A00",
      btnPrimaryText: "white",
      btnPrimaryHoverBg: "#E65A00",
      btnPrimaryActiveBg: "#CC4F00",
      btnPrimaryDisabledBg: "#FFAA66",
      btnPrimaryBorderRadius: "5px",
      tableHeaderBg: "#E0E0E0",
      tableHeaderText: "#333333",
      tableRowBg: "white",
      tableRowHoverBg: "#FFF3E0",
      tableRowBorder: "#D3D3D3",
    },
  },
  "isavia-professional-light": {
    name: "Isavia Professional Light",
    settings: {
      fontFamily: "'Montserrat', sans-serif",
      sidebarBg: "#F5F5F5",
      sidebarText: "#333333",
      sidebarHoverText: "#FF6A00",
      sidebarActiveBg: "#FFB347",
      sidebarActiveText: "white",
      sidebarBorder: "#D3D3D3",
      sidebarBorderRadius: "15px",
      sidebarLinkHoverBg: "#E0E0E0",
      headerBg: "#FFD9A3",
      headerBorder: "#FFA500",
      headerButtonBorder: "#FFA500",
      headerButtonText: "#663300",
      headerButtonHoverBg: "#FFA500",
      headerButtonHoverText: "white",
      pageBg: "#FFD699",
      cardBodyBg: "#F5F5F5",
      cardBorderRadius: "10px",
      formControlBg: "white",
      formControlText: "#333333",
      formControlBorder: "#D3D3D3",
      formControlPlaceholderColor: "#999999",
      btnPrimaryBg: "#FF6A00",
      btnPrimaryText: "white",
      btnPrimaryHoverBg: "#E65A00",
      btnPrimaryActiveBg: "#CC4F00",
      btnPrimaryDisabledBg: "#FFAA66",
      btnPrimaryBorderRadius: "5px",
      tableHeaderBg: "#E0E0E0",
      tableHeaderText: "#333333",
      tableRowBg: "white",
      tableRowHoverBg: "#FFF3E0",
      tableRowBorder: "#D3D3D3",
    },
  },
  "isavia-core": {
    name: "Isavia Core",
    settings: {
      fontFamily: "'Montserrat', sans-serif",
      // Sidebar - White base with blue accents
      sidebarBg: "#FFFFFF",
      sidebarText: "#101010",
      sidebarHoverText: "#004DF4",
      sidebarActiveBg: "#004DF4",
      sidebarActiveText: "#FFFFFF",
      sidebarBorder: "#E0E0E0",
      sidebarBorderRadius: "15px",
      sidebarLinkHoverBg: "#F0F4FF",
      // Header - Blue with white elements
      headerBg: "#004DF4",
      headerBorder: "#0040C7",
      headerButtonBorder: "#FFFFFF",
      headerButtonText: "#FFFFFF",
      headerButtonHoverBg: "#FFFFFF",
      headerButtonHoverText: "#004DF4",
      // Page & Cards - Light background
      pageBg: "#F8F9FA",
      cardBodyBg: "#FFFFFF",
      cardBorderRadius: "10px",
      // Form Controls
      formControlBg: "#FFFFFF",
      formControlText: "#101010",
      formControlBorder: "#D0D0D0",
      formControlPlaceholderColor: "#808080",
      // Buttons - Blue primary
      btnPrimaryBg: "#004DF4",
      btnPrimaryText: "#FFFFFF",
      btnPrimaryHoverBg: "#0040C7",
      btnPrimaryActiveBg: "#00339A",
      btnPrimaryDisabledBg: "#809AFF",
      btnPrimaryBorderRadius: "5px",
      // Table
      tableHeaderBg: "#F0F4FF",
      tableHeaderText: "#101010",
      tableRowBg: "#FFFFFF",
      tableRowHoverBg: "#F8FAFF",
      tableRowBorder: "#E0E0E0",
    },
  },

  // Version 2: Main Colors + Golden Accent (Gullfaxi)
  "isavia-golden": {
    name: "Isavia Golden",
    settings: {
      fontFamily: "'Montserrat', sans-serif",
      // Sidebar - White with golden accents
      sidebarBg: "#FFFFFF",
      sidebarText: "#101010",
      sidebarHoverText: "#BB8B3F",
      sidebarActiveBg: "#BB8B3F",
      sidebarActiveText: "#FFFFFF",
      sidebarBorder: "#E0D4C1",
      sidebarBorderRadius: "15px",
      sidebarLinkHoverBg: "#FFF8F0",
      // Header - Blue with golden highlights
      headerBg: "#004DF4",
      headerBorder: "#BB8B3F",
      headerButtonBorder: "#BB8B3F",
      headerButtonText: "#FFFFFF",
      headerButtonHoverBg: "#BB8B3F",
      headerButtonHoverText: "#FFFFFF",
      // Page & Cards - Warm white background
      pageBg: "#FFFBF5",
      cardBodyBg: "#FFFFFF",
      cardBorderRadius: "10px",
      // Form Controls
      formControlBg: "#FFFFFF",
      formControlText: "#101010",
      formControlBorder: "#D4C4A8",
      formControlPlaceholderColor: "#8B7355",
      // Buttons - Blue primary with golden secondary
      btnPrimaryBg: "#004DF4",
      btnPrimaryText: "#FFFFFF",
      btnPrimaryHoverBg: "#0040C7",
      btnPrimaryActiveBg: "#00339A",
      btnPrimaryDisabledBg: "#809AFF",
      btnPrimaryBorderRadius: "5px",
      // Table with golden accents
      tableHeaderBg: "#FFF3E0",
      tableHeaderText: "#101010",
      tableRowBg: "#FFFFFF",
      tableRowHoverBg: "#FFFAF0",
      tableRowBorder: "#E0D4C1",
    },
  },

  // Version 3: Main Colors + Red Alert (Hekla)
  "isavia-dynamic": {
    name: "Isavia Dynamic",
    settings: {
      fontFamily: "'Montserrat', sans-serif",
      // Sidebar - White with blue/red accents
      sidebarBg: "#FFFFFF",
      sidebarText: "#101010",
      sidebarHoverText: "#004DF4",
      sidebarActiveBg: "#DB2A12",
      sidebarActiveText: "#FFFFFF",
      sidebarBorder: "#E0E0E0",
      sidebarBorderRadius: "15px",
      sidebarLinkHoverBg: "#FFF0EE",
      // Header - Blue base with red accents
      headerBg: "#004DF4",
      headerBorder: "#0040C7",
      headerButtonBorder: "#DB2A12",
      headerButtonText: "#FFFFFF",
      headerButtonHoverBg: "#DB2A12",
      headerButtonHoverText: "#FFFFFF",
      // Page & Cards
      pageBg: "#F8F9FA",
      cardBodyBg: "#FFFFFF",
      cardBorderRadius: "10px",
      // Form Controls
      formControlBg: "#FFFFFF",
      formControlText: "#101010",
      formControlBorder: "#D0D0D0",
      formControlPlaceholderColor: "#808080",
      // Buttons - Blue primary, red for important actions
      btnPrimaryBg: "#004DF4",
      btnPrimaryText: "#FFFFFF",
      btnPrimaryHoverBg: "#0040C7",
      btnPrimaryActiveBg: "#00339A",
      btnPrimaryDisabledBg: "#809AFF",
      btnPrimaryBorderRadius: "5px",
      // Table
      tableHeaderBg: "#F0F4FF",
      tableHeaderText: "#101010",
      tableRowBg: "#FFFFFF",
      tableRowHoverBg: "#FFF5F4",
      tableRowBorder: "#E0E0E0",
    },
  },

  // Version 4: Full Spectrum (All Colors)
  "isavia-spectrum": {
    name: "Isavia Spectrum",
    settings: {
      fontFamily: "'Montserrat', sans-serif",
      // Sidebar - White with green nature accents
      sidebarBg: "#FFFFFF",
      sidebarText: "#101010",
      sidebarHoverText: "#0D9935",
      sidebarActiveBg: "#004DF4",
      sidebarActiveText: "#FFFFFF",
      sidebarBorder: "#D4E8D9",
      sidebarBorderRadius: "15px",
      sidebarLinkHoverBg: "#F0FFF4",
      // Header - Blue with golden border
      headerBg: "#004DF4",
      headerBorder: "#BB8B3F",
      headerButtonBorder: "#BB8B3F",
      headerButtonText: "#FFFFFF",
      headerButtonHoverBg: "#0D9935",
      headerButtonHoverText: "#FFFFFF",
      // Page & Cards - Subtle warm background
      pageBg: "#FAFBFA",
      cardBodyBg: "#FFFFFF",
      cardBorderRadius: "10px",
      // Form Controls with green success states
      formControlBg: "#FFFFFF",
      formControlText: "#101010",
      formControlBorder: "#C8DCC8",
      formControlPlaceholderColor: "#6B8E6B",
      // Buttons - Blue primary with color variations
      btnPrimaryBg: "#004DF4",
      btnPrimaryText: "#FFFFFF",
      btnPrimaryHoverBg: "#0040C7",
      btnPrimaryActiveBg: "#00339A",
      btnPrimaryDisabledBg: "#809AFF",
      btnPrimaryBorderRadius: "5px",
      // Table with alternating color hints
      tableHeaderBg: "#F0F8F2",
      tableHeaderText: "#101010",
      tableRowBg: "#FFFFFF",
      tableRowHoverBg: "#F8FFF9",
      tableRowBorder: "#D4E8D9",
    },
  },
  "modern-midnight-dark": {
    name: "Modern Midnight Dark",
    settings: {
      fontFamily: "'Inter', sans-serif",
      sidebarBg: "#2D3748", // Dark Blue-Gray
      sidebarText: "#E2E8F0", // Light Gray-Blue
      sidebarHoverText: "#63B3ED", // Sky Blue
      sidebarActiveBg: "#3182CE", // Medium Blue
      sidebarActiveText: "white",
      sidebarBorder: "#4A5568", // Medium Blue-Gray
      sidebarBorderRadius: "10px",
      sidebarLinkHoverBg: "#4A5568",
      headerBg: "#1A202C", // Very Dark Blue
      headerBorder: "#2D3748",
      headerButtonBorder: "#3182CE",
      headerButtonText: "#E2E8F0",
      headerButtonHoverBg: "#3182CE",
      headerButtonHoverText: "white",
      pageBg: "#1A202C", // Very Dark Blue
      cardBodyBg: "#4A5568", // Medium Blue-Gray
      cardBorderRadius: "8px",
      formControlBg: "#2D3748", // Dark Blue-Gray
      formControlText: "#E2E8F0", // Light Gray-Blue
      formControlBorder: "#718096", // Gray
      formControlPlaceholderColor: "#A0AEC0", // Lighter Gray
      btnPrimaryBg: "#3182CE", // Medium Blue
      btnPrimaryText: "white",
      btnPrimaryHoverBg: "#2B6CB0", // Darker Medium Blue
      btnPrimaryActiveBg: "#2C5282", // Dark Blue
      btnPrimaryDisabledBg: "#63B3ED", // Sky Blue (lighter for disabled)
      btnPrimaryBorderRadius: "6px",
      tableHeaderBg: "#2D3748", // Dark Blue-Gray
      tableHeaderText: "#E2E8F0", // Light Gray-Blue
      tableRowBg: "#4A5568", // Medium Blue-Gray
      tableRowHoverBg: "#718096", // Gray
      tableRowBorder: "#2D3748", // Dark Blue-Gray
    },
  },
  "oceanic-breeze": {
    name: "Oceanic Breeze",
    settings: {
      fontFamily: "'Open Sans', sans-serif",
      sidebarBg: "#E0F7FA", // Light Cyan
      sidebarText: "#004D40", // Dark Teal
      sidebarHoverText: "#00796B", // Medium Teal
      sidebarActiveBg: "#00796B", // Medium Teal
      sidebarActiveText: "white",
      sidebarBorder: "#B2EBF2", // Pale Cyan
      sidebarBorderRadius: "12px",
      sidebarLinkHoverBg: "#B2DFDB", // Lighter Teal
      headerBg: "#B2EBF2", // Pale Cyan
      headerBorder: "#4DD0E1", // Cyan
      headerButtonBorder: "#00796B",
      headerButtonText: "#004D40",
      headerButtonHoverBg: "#00796B",
      headerButtonHoverText: "white",
      pageBg: "#F0F4F8", // Very Light Blue-Gray
      cardBodyBg: "#FFFFFF",
      cardBorderRadius: "8px",
      formControlBg: "#FFFFFF",
      formControlText: "#263238", // Dark Slate
      formControlBorder: "#B0BEC5", // Blue Gray
      formControlPlaceholderColor: "#78909C", // Lighter Blue Gray
      btnPrimaryBg: "#00ACC1", // Strong Cyan
      btnPrimaryText: "white",
      btnPrimaryHoverBg: "#00838F", // Darker Cyan
      btnPrimaryActiveBg: "#006064", // Very Dark Cyan
      btnPrimaryDisabledBg: "#80DEEA", // Light Cyan
      btnPrimaryBorderRadius: "6px",
      tableHeaderBg: "#B2EBF2", // Pale Cyan
      tableHeaderText: "#004D40", // Dark Teal
      tableRowBg: "#FFFFFF",
      tableRowHoverBg: "#E0F7FA", // Light Cyan
      tableRowBorder: "#B2EBF2", // Pale Cyan
    },
  },
  "forest-canopy": {
    name: "Forest Canopy",
    settings: {
      fontFamily: "'Roboto', sans-serif",
      sidebarBg: "#2E7D32", // Dark Green
      sidebarText: "#C8E6C9", // Light Green
      sidebarHoverText: "#FFFFFF",
      sidebarActiveBg: "#66BB6A", // Medium Green
      sidebarActiveText: "#1B5E20", // Very Dark Green
      sidebarBorder: "#1B5E20", // Very Dark Green
      sidebarBorderRadius: "10px",
      sidebarLinkHoverBg: "#4CAF50", // Green
      headerBg: "#388E3C", // Forest Green
      headerBorder: "#1B5E20",
      headerButtonBorder: "#A5D6A7", // Light Green
      headerButtonText: "#FFFFFF",
      headerButtonHoverBg: "#66BB6A",
      headerButtonHoverText: "#1B5E20",
      pageBg: "#E8F5E9", // Pale Green
      cardBodyBg: "#FFFFFF",
      cardBorderRadius: "8px",
      formControlBg: "#FFFFFF",
      formControlText: "#212121", // Dark Gray
      formControlBorder: "#A5D6A7", // Light Green
      formControlPlaceholderColor: "#757575", // Medium Gray
      btnPrimaryBg: "#4CAF50", // Green
      btnPrimaryText: "white",
      btnPrimaryHoverBg: "#388E3C", // Darker Green
      btnPrimaryActiveBg: "#2E7D32", // Even Darker Green
      btnPrimaryDisabledBg: "#A5D6A7", // Light Green
      btnPrimaryBorderRadius: "6px",
      tableHeaderBg: "#C8E6C9", // Light Green
      tableHeaderText: "#1B5E20", // Very Dark Green
      tableRowBg: "#FFFFFF",
      tableRowHoverBg: "#E8F5E9", // Pale Green
      tableRowBorder: "#A5D6A7", // Light Green
    },
  },
  "sunset-glow": {
    name: "Sunset Glow",
    settings: {
      fontFamily: "'Poppins', sans-serif",
      sidebarBg: "#424242", // Dark Gray
      sidebarText: "#FFCC80", // Light Orange
      sidebarHoverText: "#FFA726", // Orange
      sidebarActiveBg: "#FF7043", // Coral
      sidebarActiveText: "#FFFFFF",
      sidebarBorder: "#616161", // Darker Gray
      sidebarBorderRadius: "12px",
      sidebarLinkHoverBg: "#616161", // Darker Gray
      headerBg: "#5D4037", // Dark Brown
      headerBorder: "#4E342E", // Darker Brown
      headerButtonBorder: "#FFCC80", // Light Orange
      headerButtonText: "#FFFFFF",
      headerButtonHoverBg: "#FFB74D", // Lighter Orange
      headerButtonHoverText: "#424242",
      pageBg: "#FFF3E0", // Pale Orange/Cream
      cardBodyBg: "#FFFFFF",
      cardBorderRadius: "10px",
      formControlBg: "#FFFFFF",
      formControlText: "#4E342E", // Dark Brown
      formControlBorder: "#FFCC80", // Light Orange
      formControlPlaceholderColor: "#A1887F", // Medium Brown
      btnPrimaryBg: "#FF5722", // Deep Orange
      btnPrimaryText: "white",
      btnPrimaryHoverBg: "#E64A19", // Darker Deep Orange
      btnPrimaryActiveBg: "#D84315", // Even Darker
      btnPrimaryDisabledBg: "#FFAB91", // Light Coral
      btnPrimaryBorderRadius: "5px",
      tableHeaderBg: "#FFCC80", // Light Orange
      tableHeaderText: "#5D4037", // Dark Brown
      tableRowBg: "#FFFFFF",
      tableRowHoverBg: "#FFF3E0", // Pale Orange
      tableRowBorder: "#FFE0B2", // Very Pale Orange
    },
  },
  "charcoal-crimson": {
    name: "Charcoal & Crimson",
    settings: {
      fontFamily: "'Inter', sans-serif",
      sidebarBg: "#212121", // Near Black
      sidebarText: "#BDBDBD", // Medium Gray
      sidebarHoverText: "#E53935", // Red
      sidebarActiveBg: "#D32F2F", // Dark Red
      sidebarActiveText: "white",
      sidebarBorder: "#424242", // Dark Gray
      sidebarBorderRadius: "8px",
      sidebarLinkHoverBg: "#424242", // Dark Gray
      headerBg: "#303030", // Very Dark Gray
      headerBorder: "#000000", // Black
      headerButtonBorder: "#D32F2F", // Dark Red
      headerButtonText: "#FFFFFF",
      headerButtonHoverBg: "#E53935", // Red
      headerButtonHoverText: "white",
      pageBg: "#303030", 
      cardBodyBg: "#424242", 
      cardBorderRadius: "6px",
      formControlBg: "#616161", 
      formControlText: "#FFFFFF",
      formControlBorder: "#757575", 
      formControlPlaceholderColor: "#BDBDBD", 
      btnPrimaryBg: "#C62828", // Strong Red
      btnPrimaryText: "white",
      btnPrimaryHoverBg: "#B71C1C", // Very Dark Red
      btnPrimaryActiveBg: "#A50F0F", // Even Darker Red
      btnPrimaryDisabledBg: "#EF9A9A", // Light Red
      btnPrimaryBorderRadius: "4px",
      tableHeaderBg: "#424242", 
      tableHeaderText: "#FFFFFF",
      tableRowBg: "#616161", 
      tableRowHoverBg: "#757575", 
      tableRowBorder: "#212121", 
    },
  },
  "lavender-fields": {
    name: "Lavender Fields",
    settings: {
      fontFamily: "'Montserrat', sans-serif",
      sidebarBg: "#EDE7F6", // Very Light Lavender
      sidebarText: "#5E35B1", // Deep Purple
      sidebarHoverText: "#7E57C2", // Medium Purple
      sidebarActiveBg: "#673AB7", // Purple
      sidebarActiveText: "white",
      sidebarBorder: "#D1C4E9", // Light Lavender
      sidebarBorderRadius: "15px",
      sidebarLinkHoverBg: "#D1C4E9", 
      headerBg: "#D1C4E9", 
      headerBorder: "#B39DDB", 
      headerButtonBorder: "#7E57C2", 
      headerButtonText: "#4527A0", 
      headerButtonHoverBg: "#7E57C2",
      headerButtonHoverText: "white",
      pageBg: "#F3E5F5", // Pale Lilac
      cardBodyBg: "#FFFFFF",
      cardBorderRadius: "10px",
      formControlBg: "#FFFFFF",
      formControlText: "#4A148C", 
      formControlBorder: "#CE93D8", 
      formControlPlaceholderColor: "#AB47BC", 
      btnPrimaryBg: "#7E57C2", 
      btnPrimaryText: "white",
      btnPrimaryHoverBg: "#673AB7", 
      btnPrimaryActiveBg: "#5E35B1", 
      btnPrimaryDisabledBg: "#B39DDB", 
      btnPrimaryBorderRadius: "6px",
      tableHeaderBg: "#E1BEE7", 
      tableHeaderText: "#6A1B9A", 
      tableRowBg: "#FFFFFF",
      tableRowHoverBg: "#F3E5F5", 
      tableRowBorder: "#D1C4E9", 
    },
  },
  "minimalist-monochrome": {
    name: "Minimalist Monochrome",
    settings: {
      fontFamily: "'Inter', sans-serif",
      sidebarBg: "#F5F5F5", // Light Gray
      sidebarText: "#212121", // Near Black
      sidebarHoverText: "#000000", // Black
      sidebarActiveBg: "#424242", // Dark Gray
      sidebarActiveText: "white",
      sidebarBorder: "#E0E0E0", // Lighter Gray
      sidebarBorderRadius: "0px", // Sharp edges
      sidebarLinkHoverBg: "#E0E0E0", 
      headerBg: "#EEEEEE", 
      headerBorder: "#BDBDBD", 
      headerButtonBorder: "#616161", 
      headerButtonText: "#212121",
      headerButtonHoverBg: "#424242",
      headerButtonHoverText: "white",
      pageBg: "#FFFFFF",
      cardBodyBg: "#FAFAFA", 
      cardBorderRadius: "4px",
      formControlBg: "#FFFFFF",
      formControlText: "#212121",
      formControlBorder: "#E0E0E0",
      formControlPlaceholderColor: "#9E9E9E", 
      btnPrimaryBg: "#212121", 
      btnPrimaryText: "white",
      btnPrimaryHoverBg: "#000000", 
      btnPrimaryActiveBg: "#424242", 
      btnPrimaryDisabledBg: "#BDBDBD", 
      btnPrimaryBorderRadius: "2px", 
      tableHeaderBg: "#EEEEEE", 
      tableHeaderText: "#212121",
      tableRowBg: "#FFFFFF",
      tableRowHoverBg: "#F5F5F5", 
      tableRowBorder: "#E0E0E0", 
    },
  },
  "desert-mirage": {
    name: "Desert Mirage",
    settings: {
      fontFamily: "'Roboto', sans-serif",
      sidebarBg: "#D7CCC8", // Light Brown Gray
      sidebarText: "#4E342E", // Dark Brown
      sidebarHoverText: "#795548", // Brown
      sidebarActiveBg: "#A1887F", // Medium Brown
      sidebarActiveText: "#FFFFFF",
      sidebarBorder: "#BCAAA4", // Light Brown
      sidebarBorderRadius: "10px",
      sidebarLinkHoverBg: "#BCAAA4", 
      headerBg: "#BCAAA4", 
      headerBorder: "#8D6E63", 
      headerButtonBorder: "#795548", 
      headerButtonText: "#FFFFFF",
      headerButtonHoverBg: "#795548",
      headerButtonHoverText: "#FFFFFF",
      pageBg: "#FFF8E1", // Pale Yellow/Sand
      cardBodyBg: "#FFFFFF",
      cardBorderRadius: "8px",
      formControlBg: "#FFFFFF",
      formControlText: "#4E342E",
      formControlBorder: "#D7CCC8", 
      formControlPlaceholderColor: "#A1887F", 
      btnPrimaryBg: "#0277BD", 
      btnPrimaryText: "white",
      btnPrimaryHoverBg: "#01579B", 
      btnPrimaryActiveBg: "#00427A", 
      btnPrimaryDisabledBg: "#81D4FA", 
      btnPrimaryBorderRadius: "6px",
      tableHeaderBg: "#D7CCC8", 
      tableHeaderText: "#4E342E",
      tableRowBg: "#FFFFFF",
      tableRowHoverBg: "#FFF8E1", 
      tableRowBorder: "#D7CCC8", 
    },
  },
  "nordic-night": {
    name: "Nordic Night",
    settings: {
      fontFamily: "'Open Sans', sans-serif",
      sidebarBg: "#263238", // Dark Slate Blue
      sidebarText: "#B0BEC5", // Light Blue Gray
      sidebarHoverText: "#81D4FA", // Light Blue
      sidebarActiveBg: "#039BE5", // Bright Blue
      sidebarActiveText: "white",
      sidebarBorder: "#37474F", // Blue Gray
      sidebarBorderRadius: "8px",
      sidebarLinkHoverBg: "#37474F", 
      headerBg: "#37474F", 
      headerBorder: "#102A43", 
      headerButtonBorder: "#81D4FA", 
      headerButtonText: "#ECEFF1", 
      headerButtonHoverBg: "#039BE5",
      headerButtonHoverText: "white",
      pageBg: "#102A43", 
      cardBodyBg: "#263238", 
      cardBorderRadius: "6px",
      formControlBg: "#37474F", 
      formControlText: "#ECEFF1",
      formControlBorder: "#546E7A", 
      formControlPlaceholderColor: "#90A4AE", 
      btnPrimaryBg: "#03A9F4", // Sky Blue
      btnPrimaryText: "white",
      btnPrimaryHoverBg: "#0288D1", 
      btnPrimaryActiveBg: "#01579B", 
      btnPrimaryDisabledBg: "#4FC3F7", 
      btnPrimaryBorderRadius: "5px",
      tableHeaderBg: "#37474F", 
      tableHeaderText: "#ECEFF1",
      tableRowBg: "#263238", 
      tableRowHoverBg: "#455A64", 
      tableRowBorder: "#37474F", 
    },
  },
  "vintage-paper": {
    name: "Vintage Paper",
    settings: {
      fontFamily: "'Times New Roman', serif",
      sidebarBg: "#F5F5DC", // Beige
      sidebarText: "#5D4037", // Dark Brown
      sidebarHoverText: "#A1887F", // Medium Brown
      sidebarActiveBg: "#8D6E63", // Sepia Brown
      sidebarActiveText: "#F5F5F5", 
      sidebarBorder: "#D2B48C", // Tan
      sidebarBorderRadius: "5px",
      sidebarLinkHoverBg: "#EFEBE9", 
      headerBg: "#EFEBE9", 
      headerBorder: "#C0A080", 
      headerButtonBorder: "#8D6E63", 
      headerButtonText: "#5D4037",
      headerButtonHoverBg: "#8D6E63",
      headerButtonHoverText: "#FFFFFF",
      pageBg: "#FAF0E6", // Linen / Pale Cream
      cardBodyBg: "#FFF9C4", // Pale Yellow
      cardBorderRadius: "4px",
      formControlBg: "#FFFFFF",
      formControlText: "#5D4037",
      formControlBorder: "#D2B48C", 
      formControlPlaceholderColor: "#A1887F", 
      btnPrimaryBg: "#795548", // Brown
      btnPrimaryText: "#FFFFFF",
      btnPrimaryHoverBg: "#5D4037", 
      btnPrimaryActiveBg: "#4E342E", 
      btnPrimaryDisabledBg: "#BCAAA4", 
      btnPrimaryBorderRadius: "3px",
      tableHeaderBg: "#F5F5DC", 
      tableHeaderText: "#5D4037",
      tableRowBg: "#FFF9C4", 
      tableRowHoverBg: "#FAF0E6", 
      tableRowBorder: "#D2B48C", 
    },
  },
  "cyber-glow": {
    name: "Cyber Glow",
    settings: {
      fontFamily: "'Poppins', sans-serif",
      sidebarBg: "#1A001A", // Very Dark Purple
      sidebarText: "#E0B0FF", // Light Purple
      sidebarHoverText: "#00FFFF", // Cyan
      sidebarActiveBg: "#FF00FF", // Magenta
      sidebarActiveText: "#000000", 
      sidebarBorder: "#3C003C", // Dark Purple
      sidebarBorderRadius: "10px",
      sidebarLinkHoverBg: "#2E002E", 
      headerBg: "#240032", 
      headerBorder: "#4A004A", 
      headerButtonBorder: "#00FFFF", 
      headerButtonText: "#FFFFFF",
      headerButtonHoverBg: "#00FFFF",
      headerButtonHoverText: "#000000",
      pageBg: "#0D000D", // Almost Black Purple
      cardBodyBg: "#1F0029", 
      cardBorderRadius: "8px",
      formControlBg: "#2A0036", 
      formControlText: "#F0D0FF", 
      formControlBorder: "#6A006A", 
      formControlPlaceholderColor: "#A070C0", 
      btnPrimaryBg: "#39FF14", // Neon Lime Green
      btnPrimaryText: "#000000", 
      btnPrimaryHoverBg: "#28E00A", 
      btnPrimaryActiveBg: "#1CC000", 
      btnPrimaryDisabledBg: "#A0FFA0", 
      btnPrimaryBorderRadius: "5px",
      tableHeaderBg: "#2E002E", 
      tableHeaderText: "#00FFFF", 
      tableRowBg: "#1F0029", 
      tableRowHoverBg: "#3C003C", 
      tableRowBorder: "#4A004A", 
    },
  },
};

const EDITABLE_PROPERTIES_CONFIG = {
  Global: {
    fontFamily: { label: "Font Family", type: "select", options: [
      "'KEF Oasis Web', sans-serif", 
      "'KEF Oasis Web Variable', sans-serif",
      "'KEF Oasis Web Thin', sans-serif",
      "'KEF Oasis Web Light', sans-serif", 
      "'KEF Oasis Web Regular', sans-serif",
      "'KEF Oasis Web Medium', sans-serif",
      "'KEF Oasis Web SemiBold', sans-serif",
      "'KEF Oasis Web Bold', sans-serif",
      "'Inter', sans-serif", 
      "'Montserrat', sans-serif", 
      "'Poppins', sans-serif", 
      "'Roboto', sans-serif", 
      "'Open Sans', sans-serif", 
      "'Times New Roman', serif"
    ] },
    pageBg: { label: "Page Background", type: "color" },
  },
  Sidebar: {
    sidebarBg: { label: "Background", type: "color" },
    sidebarText: { label: "Text Color", type: "color" },
    sidebarHoverText: { label: "Link Hover Text", type: "color" },
    sidebarLinkHoverBg: { label: "Link Hover BG", type: "color" },
    sidebarActiveBg: { label: "Active Item BG", type: "color" },
    sidebarActiveText: { label: "Active Item Text", type: "color" },
    sidebarBorder: { label: "Border Color", type: "color" },
    sidebarBorderRadius: { label: "Border Radius (e.g., 15px)", type: "text" },
  },
  Header: {
    headerBg: { label: "Background", type: "color" },
    headerBorder: { label: "Border Color", type: "color" },
    headerButtonBorder: { label: "Button Border", type: "color" },
    headerButtonText: { label: "Button Text", type: "color" },
    headerButtonHoverBg: { label: "Button Hover BG", type: "color" },
    headerButtonHoverText: { label: "Button Hover Text", type: "color" },
  },
  CardsAndForms: {
    cardBodyBg: { label: "Card Background", type: "color" },
    cardBorderRadius: { label: "Card Border Radius", type: "text" },
    formControlBg: { label: "Input Background", type: "color" },
    formControlText: { label: "Input Text", type: "color" },
    formControlBorder: { label: "Input Border", type: "color" },
    formControlPlaceholderColor: { label: "Input Placeholder Text", type: "color" },
  },
  ButtonsPrimary: {
    btnPrimaryBg: { label: "Background", type: "color" },
    btnPrimaryText: { label: "Text Color", type: "color" },
    btnPrimaryHoverBg: { label: "Hover BG", type: "color" },
    btnPrimaryActiveBg: { label: "Active BG", type: "color" },
    btnPrimaryDisabledBg: { label: "Disabled BG", type: "color" },
    btnPrimaryBorderRadius: { label: "Border Radius", type: "text" },
  },
  Tables: {
    tableHeaderBg: { label: "Header Background", type: "color" },
    tableHeaderText: { label: "Header Text", type: "color" },
    tableRowBg: { label: "Row Background", type: "color" },
    tableRowHoverBg: { label: "Row Hover BG", type: "color" },
    tableRowBorder: { label: "Row Border Color", type: "color" },
  }
};

// --- Gemini API Integration ---
const PALETTE_SUGGESTION_SCHEMA = {
  type: "OBJECT",
  properties: {
    pageBg: { type: "STRING", description: "Hex color for the main page background." },
    sidebarBg: { type: "STRING", description: "Hex color for the sidebar background." },
    sidebarText: { type: "STRING", description: "Hex color for the sidebar text." },
    headerBg: { type: "STRING", description: "Hex color for the header background." },
    cardBodyBg: { type: "STRING", description: "Hex color for card backgrounds." },
    btnPrimaryBg: { type: "STRING", description: "Hex color for primary buttons." },
    btnPrimaryText: { type: "STRING", description: "Hex color for primary button text." },
  },
  required: ["pageBg", "sidebarBg", "sidebarText", "headerBg", "cardBodyBg", "btnPrimaryBg", "btnPrimaryText"]
};


const generateCssFromSettings = (settings) => {
  let bodyTextColor = '#1F2937'; 
  if (settings.pageBg) {
    const r = parseInt(settings.pageBg.slice(1, 3), 16);
    const g = parseInt(settings.pageBg.slice(3, 5), 16);
    const b = parseInt(settings.pageBg.slice(5, 7), 16);
    if ((r * 0.299 + g * 0.587 + b * 0.114) < 128) { 
        bodyTextColor = '#E2E8F0'; 
    }
  }
  if (settings.fontFamily && (settings.fontFamily.includes('Inter') || settings.fontFamily.includes('Roboto'))) {
     if (settings.pageBg === '#1A202C' || settings.pageBg === '#0D000D' || settings.pageBg === '#102A43' || settings.pageBg === '#303030' || settings.pageBg === '#212121') { 
        bodyTextColor = '#E2E8F0';
     } else {
        bodyTextColor = '#1F2937';
     }
  }

  // Generate @font-face declarations for KEF fonts if they're being used
  let fontFaceDeclarations = '';
  if (settings.fontFamily && settings.fontFamily.includes('KEF Oasis Web')) {
    const baseUrl = window.location.origin; // Gets the current app's URL
    fontFaceDeclarations = `
    /* KEF Oasis Web Font Family - Served from Theme Customizer */
    @font-face {
      font-family: 'KEF Oasis Web';
      src: url('${baseUrl}/KEFOasisWeb-Thin.woff') format('woff');
      font-weight: 100;
      font-style: normal;
      font-display: swap;
    }
    @font-face {
      font-family: 'KEF Oasis Web';
      src: url('${baseUrl}/KEFOasisWeb-Light.woff') format('woff');
      font-weight: 300;
      font-style: normal;
      font-display: swap;
    }
    @font-face {
      font-family: 'KEF Oasis Web';
      src: url('${baseUrl}/KEFOasisWeb-Regular.woff') format('woff');
      font-weight: 400;
      font-style: normal;
      font-display: swap;
    }
    @font-face {
      font-family: 'KEF Oasis Web';
      src: url('${baseUrl}/KEFOasisWeb-Medium.woff') format('woff');
      font-weight: 500;
      font-style: normal;
      font-display: swap;
    }
    @font-face {
      font-family: 'KEF Oasis Web';
      src: url('${baseUrl}/KEFOasisWeb-SemiBold.woff') format('woff');
      font-weight: 600;
      font-style: normal;
      font-display: swap;
    }
    @font-face {
      font-family: 'KEF Oasis Web';
      src: url('${baseUrl}/KEFOasisWeb-Bold.woff') format('woff');
      font-weight: 700;
      font-style: normal;
      font-display: swap;
    }
    @font-face {
      font-family: 'KEF Oasis Web Variable';
      src: url('${baseUrl}/KEFOasisWeb-Variable.woff') format('woff');
      font-weight: 100 900;
      font-style: normal;
      font-display: swap;
    }
    
    /* Individual Weight Families */
    @font-face {
      font-family: 'KEF Oasis Web Thin';
      src: url('${baseUrl}/KEFOasisWeb-Thin.woff') format('woff');
      font-weight: 100;
      font-style: normal;
      font-display: swap;
    }
    @font-face {
      font-family: 'KEF Oasis Web Light';
      src: url('${baseUrl}/KEFOasisWeb-Light.woff') format('woff');
      font-weight: 300;
      font-style: normal;
      font-display: swap;
    }
    @font-face {
      font-family: 'KEF Oasis Web Regular';
      src: url('${baseUrl}/KEFOasisWeb-Regular.woff') format('woff');
      font-weight: 400;
      font-style: normal;
      font-display: swap;
    }
    @font-face {
      font-family: 'KEF Oasis Web Medium';
      src: url('${baseUrl}/KEFOasisWeb-Medium.woff') format('woff');
      font-weight: 500;
      font-style: normal;
      font-display: swap;
    }
    @font-face {
      font-family: 'KEF Oasis Web SemiBold';
      src: url('${baseUrl}/KEFOasisWeb-SemiBold.woff') format('woff');
      font-weight: 600;
      font-style: normal;
      font-display: swap;
    }
    @font-face {
      font-family: 'KEF Oasis Web Bold';
      src: url('${baseUrl}/KEFOasisWeb-Bold.woff') format('woff');
      font-weight: 700;
      font-style: normal;
      font-display: swap;
    }
    `;
  }

  return `${fontFaceDeclarations}
    /* --- Generated Theme: ${settings.name || 'Customized Theme'} --- */

    body, .DataCentralPreviewContainer {
      font-family: ${settings.fontFamily || "'Inter', sans-serif"} !important;
      background-color: ${settings.pageBg || '#FFFFFF'} !important;
      color: ${bodyTextColor} !important;
    }

    #kt_app_sidebar {
      background-color: ${settings.sidebarBg} !important;
      border-right: 2px solid ${settings.sidebarBorder} !important;
      border-radius: ${settings.sidebarBorderRadius} !important;
      padding: 15px !important;
      transition: all 0.3s ease-in-out;
    }
    #kt_app_sidebar a, #kt_app_sidebar .menu-title, #kt_app_sidebar .menu-icon i {
      color: ${settings.sidebarText} !important;
      transition: color 0.3s ease-in-out, background-color 0.3s ease-in-out;
    }
    #kt_app_sidebar a:hover {
      color: ${settings.sidebarHoverText} !important;
    }
    #kt_app_sidebar_menu_wrapper .menu-link {
      padding: 10px 15px !important;
      border-radius: 6px !important;
      margin-bottom: 4px !important;
      display: flex !important;
      align-items: center !important;
      text-decoration: none !important;
      position: relative !important;
      z-index: 1 !important;
      flex-wrap: nowrap !important;
    }
    #kt_app_sidebar_menu_wrapper .menu-link.active,
    #kt_app_sidebar_menu_wrapper .menu-link.routerlinkactive {
      background-color: ${settings.sidebarActiveBg} !important;
      color: ${settings.sidebarActiveText} !important;
      font-weight: 600 !important;
    }
    #kt_app_sidebar_menu_wrapper .menu-link.active .menu-icon i,
    #kt_app_sidebar_menu_wrapper .menu-link.routerlinkactive .menu-icon i,
    #kt_app_sidebar_menu_wrapper .menu-link.active .menu-title,
    #kt_app_sidebar_menu_wrapper .menu-link.routerlinkactive .menu-title {
      color: ${settings.sidebarActiveText} !important;
    }
    #kt_app_sidebar_menu_wrapper .menu-link:hover:not(.active):not(.routerlinkactive) {
      background-color: ${settings.sidebarLinkHoverBg || 'rgba(0,0,0,0.05)'} !important;
      color: ${settings.sidebarHoverText} !important;
      z-index: 2 !important;
      transform: translateX(2px) !important;
      transition: all 0.2s ease-in-out !important;
    }
    #kt_app_sidebar_menu_wrapper .menu-link:hover:not(.active):not(.routerlinkactive) .menu-icon i,
    #kt_app_sidebar_menu_wrapper .menu-link:hover:not(.active):not(.routerlinkactive) .menu-title {
       color: ${settings.sidebarHoverText} !important;
    }
    #kt_app_sidebar_menu_wrapper .menu-title {
      font-weight: 500 !important;
      white-space: nowrap !important;
      flex-shrink: 0 !important;
    }
    
    /* Production site menu structure support */
    #kt_app_sidebar_menu_wrapper .menu-icon {
      display: inline-flex !important;
      align-items: center !important;
      justify-content: center !important;
      margin-right: 12px !important;
      flex-shrink: 0 !important;
      width: 20px !important;
      height: 20px !important;
    }
    
    #kt_app_sidebar_menu_wrapper .menu-icon i {
      font-size: 16px !important;
      line-height: 1 !important;
    }
    
    #kt_app_sidebar_menu_wrapper .menu-arrow {
      margin-left: auto !important;
      flex-shrink: 0 !important;
    }
    
    /* Ensure menu items stay on one line */
    .menu-item .menu-link {
      white-space: nowrap !important;
      overflow: visible !important;
    }

    #kt_app_header_wrapper {
      background-color: ${settings.headerBg} !important;
      border-bottom: 2px solid ${settings.headerBorder} !important;
      padding: 10px 20px !important;
      display: flex !important;
      align-items: center !important;
      justify-content: space-between !important;
      transition: all 0.3s ease-in-out;
    }
    #kt_app_header_container {
        background-color: ${settings.headerBg} !important;
    }
    #kt_app_header_wrapper .btn-header {
      border: 1px solid ${settings.headerButtonBorder} !important;
      color: ${settings.headerButtonText} !important;
      background-color: transparent !important;
      border-radius: 50% !important;
      width: 40px !important;
      height: 40px !important;
      display: inline-flex !important;
      align-items: center !important;
      justify-content: center !important;
      transition: all 0.2s ease-in-out;
    }
    #kt_app_header_wrapper .btn-header:hover {
      background-color: ${settings.headerButtonHoverBg} !important;
      color: ${settings.headerButtonHoverText} !important;
      transform: scale(1.1);
    }

    #kt_app_page {
        background-color: ${settings.pageBg || '#FFFFFF'} !important;
        padding: 20px !important;
        flex-grow: 1;
        transition: background-color 0.3s ease-in-out;
    }
    #kt_app_footer {
        background-color: ${settings.headerBg} !important; 
        color: ${settings.headerButtonText} !important; 
        padding: 15px 20px !important;
        border-top: 1px solid ${settings.headerBorder} !important;
        text-align: center !important;
        font-size: 0.875rem !important;
    }

    .card-body {
      background-color: ${settings.cardBodyBg} !important;
      border-radius: ${settings.cardBorderRadius} !important;
      padding: 20px !important;
      box-shadow: 0 4px 6px -1px rgba(0,0,0,0.1), 0 2px 4px -1px rgba(0,0,0,0.06) !important;
      margin-bottom: 20px !important;
      transition: all 0.3s ease-in-out;
      color: ${settings.formControlText || bodyTextColor} !important;
    }
    .card-body h2 {
        color: ${settings.formControlText || bodyTextColor} !important;
    }
    .form-control {
      background-color: ${settings.formControlBg} !important;
      color: ${settings.formControlText} !important;
      border: 1px solid ${settings.formControlBorder} !important;
      border-radius: 6px !important;
      padding: 10px 12px !important;
      width: 100% !important;
      box-sizing: border-box !important;
      transition: border-color 0.2s ease-in-out, box-shadow 0.2s ease-in-out;
    }
    .form-control::placeholder {
      color: ${settings.formControlPlaceholderColor || '#999999'} !important;
    }
    .form-control:focus {
        border-color: ${settings.btnPrimaryBg || '#FF6A00'} !important;
        box-shadow: 0 0 0 0.2rem ${settings.btnPrimaryBg ? settings.btnPrimaryBg + '40' : '#FF6A0040'} !important;
        outline: none !important;
    }

    .btn-primary {
      background-color: ${settings.btnPrimaryBg} !important;
      color: ${settings.btnPrimaryText} !important;
      border: none !important;
      font-weight: 500 !important;
      padding: 10px 20px !important;
      border-radius: ${settings.btnPrimaryBorderRadius} !important;
      transition: background-color 0.2s ease-in-out, transform 0.1s ease-in-out;
      cursor: pointer !important;
      display: inline-flex !important;
      align-items: center !important;
      justify-content: center !important;
      gap: 8px !important;
    }
    .btn-primary:hover {
      background-color: ${settings.btnPrimaryHoverBg} !important;
      transform: translateY(-1px);
    }
    .btn-primary:active {
      background-color: ${settings.btnPrimaryActiveBg || settings.btnPrimaryHoverBg} !important;
      transform: translateY(0px);
    }
     .btn-primary:disabled {
      background-color: ${settings.btnPrimaryDisabledBg || settings.btnPrimaryBg} !important;
      color: ${settings.btnPrimaryText} !important;
      opacity: 0.6 !important;
      cursor: not-allowed !important;
      transform: none !important;
    }

    .primeng-datatable-container {
      background-color: ${settings.cardBodyBg} !important; 
      border-radius: ${settings.cardBorderRadius} !important;
      padding: 0px !important; 
      overflow: hidden !important; 
    }
    .p-datatable { width: 100% !important; border-collapse: collapse !important; }
    .p-datatable-thead th {
      background-color: ${settings.tableHeaderBg} !important;
      color: ${settings.tableHeaderText} !important;
      font-weight: 600 !important;
      padding: 12px 15px !important;
      text-align: left !important;
      border-bottom: 2px solid ${settings.tableRowBorder || '#D3D3D3'} !important;
    }
    .p-datatable-tbody tr {
      background-color: ${settings.tableRowBg} !important;
      color: ${settings.formControlText} !important; 
      border-bottom: 1px solid ${settings.tableRowBorder || '#D3D3D3'} !important;
      transition: background-color 0.2s ease-in-out;
    }
    .p-datatable-tbody tr:last-child {
      border-bottom: none !important;
    }
    .p-datatable-tbody tr:hover {
      background-color: ${settings.tableRowHoverBg} !important;
    }
    .p-datatable-tbody td {
        padding: 12px 15px !important;
    }

    .p-paginator {
        background-color: ${settings.cardBodyBg || '#F5F5F5'} !important; 
        padding: 10px 15px !important;
        border-top: 1px solid ${settings.tableRowBorder || '#D3D3D3'} !important;
        display: flex !important;
        align-items: center !important;
        justify-content: space-between !important;
        color: ${settings.formControlText} !important; 
    }
    .p-paginator button {
        background-color: ${settings.btnPrimaryBg} !important;
        color: ${settings.btnPrimaryText} !important;
        border: none !important;
        padding: 6px 12px !important;
        border-radius: ${settings.btnPrimaryBorderRadius} !important;
        transition: background-color 0.2s ease-in-out;
    }
    .p-paginator button:hover:not(:disabled) {
        background-color: ${settings.btnPrimaryHoverBg} !important;
    }
    .p-paginator button:disabled {
        background-color: ${settings.btnPrimaryDisabledBg || '#D3D3D3'} !important;
        opacity: 0.6 !important;
        cursor: not-allowed !important;
    }
  `;
};

const ThemeContext = createContext();

const ThemeProvider = ({ children }) => {
  const [activeThemeId, setActiveThemeId] = useState(Object.keys(THEME_DEFINITIONS)[0]);
  const [currentSettings, setCurrentSettings] = useState(
    JSON.parse(JSON.stringify(THEME_DEFINITIONS[activeThemeId].settings))
  );

  useEffect(() => {
    setCurrentSettings(JSON.parse(JSON.stringify(THEME_DEFINITIONS[activeThemeId].settings)));
  }, [activeThemeId]);

  const updateSetting = (key, value) => {
    setCurrentSettings(prev => ({ ...prev, [key]: value }));
  };
  
  const updateMultipleSettings = (newSettings) => {
    setCurrentSettings(prev => ({ ...prev, ...newSettings }));
  };

  const resetCurrentTheme = () => {
    setCurrentSettings(JSON.parse(JSON.stringify(THEME_DEFINITIONS[activeThemeId].settings)));
  };

  const value = {
    activeThemeId,
    setActiveThemeId,
    currentSettings,
    updateSetting,
    updateMultipleSettings, // Add this to context
    resetCurrentTheme,
    availableThemes: THEME_DEFINITIONS,
  };

  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
};

const useTheme = () => useContext(ThemeContext);

// --- Components ---

const ColorInput = ({ label, value, onChange, propertyKey }) => (
  <div className="mb-3">
    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">{label}</label>
    <div className="flex items-center space-x-2">
      <input
        type="color"
        value={value || '#ffffff'} 
        onChange={(e) => onChange(propertyKey, e.target.value)}
        className="w-8 h-8 p-0 border-none rounded cursor-pointer"
      />
      <input
        type="text"
        value={value || ''}
        onChange={(e) => onChange(propertyKey, e.target.value)}
        className="w-full px-2 py-1 text-sm border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
        placeholder="#RRGGBB"
      />
    </div>
  </div>
);

const TextInput = ({ label, value, onChange, propertyKey }) => (
  <div className="mb-3">
    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">{label}</label>
    <input
      type="text"
      value={value || ''}
      onChange={(e) => onChange(propertyKey, e.target.value)}
      className="w-full px-2 py-1.5 text-sm border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
    />
  </div>
);

const SelectInput = ({ label, value, onChange, propertyKey, options }) => (
  <div className="mb-3">
    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">{label}</label>
    <select
      value={value || ''}
      onChange={(e) => onChange(propertyKey, e.target.value)}
      className="w-full px-2 py-1.5 text-sm border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
    >
      {options.map(opt => <option key={opt} value={opt}>{opt.split(',')[0].replace(/'/g, '')}</option>)}
    </select>
  </div>
);

const AccordionSection = ({ title, children, defaultOpen = true }) => {
  const [isOpen, setIsOpen] = useState(defaultOpen);
  return (
    <div className="border-b border-gray-200 dark:border-gray-700">
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="flex justify-between items-center w-full py-3 px-1 text-left text-sm font-medium text-indigo-600 dark:text-indigo-400 hover:text-indigo-800 dark:hover:text-indigo-300 focus:outline-none"
      >
        <span>{title}</span>
        {isOpen ? <ChevronUp size={18} /> : <ChevronDown size={18} />}
      </button>
      {isOpen && <div className="pb-3 px-1">{children}</div>}
    </div>
  );
};

const AIPaletteModal = ({ isOpen, onClose, onSuggest }) => {
  const [prompt, setPrompt] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async () => {
    if (!prompt.trim()) {
      setError("Please enter a description for your theme.");
      return;
    }
    setError('');
    setIsLoading(true);

    let chatHistory = [{ role: "user", parts: [{ text: `Suggest a color palette (hex codes) for a web application theme based on the following description: "${prompt}". Provide colors for page background, sidebar background, sidebar text, header background, card background, primary button background, and primary button text.` }] }];
    const payload = {
      contents: chatHistory,
      generationConfig: {
        responseMimeType: "application/json",
        responseSchema: PALETTE_SUGGESTION_SCHEMA,
      }
    };
    const apiKey = ""; // Leave empty, Canvas will provide
    const apiUrl = `https://generativelanguage.googleapis.com/v1beta/models/gemini-2.0-flash:generateContent?key=${apiKey}`;

    try {
      const response = await fetch(apiUrl, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      });
      if (!response.ok) {
        const errorData = await response.json();
        console.error("Gemini API Error Data:", errorData);
        throw new Error(`API Error: ${response.status} ${response.statusText}. ${errorData?.error?.message || 'Unknown error'}`);
      }
      const result = await response.json();
      
      if (result.candidates && result.candidates.length > 0 &&
          result.candidates[0].content && result.candidates[0].content.parts &&
          result.candidates[0].content.parts.length > 0) {
        const jsonText = result.candidates[0].content.parts[0].text;
        const suggestedPalette = JSON.parse(jsonText);
        onSuggest(suggestedPalette); 
        onClose();
      } else {
        console.error("Unexpected API response structure:", result);
        setError("Failed to get palette suggestion. Unexpected response from AI.");
      }
    } catch (err) {
      console.error("Error suggesting palette:", err);
      setError(err.message || "Failed to suggest palette. Check console for details.");
    } finally {
      setIsLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white dark:bg-gray-800 p-6 rounded-lg shadow-xl w-full max-w-md">
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100">✨ Suggest Palette with AI</h2>
          <button onClick={onClose} className="text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200">
            <X size={20} />
          </button>
        </div>
        <p className="text-sm text-gray-600 dark:text-gray-300 mb-3">
          Describe the mood, style, or purpose of your theme (e.g., "calm and professional", "energetic gaming theme", "minimalist blog").
        </p>
        <textarea
          value={prompt}
          onChange={(e) => setPrompt(e.target.value)}
          placeholder="e.g., A sleek theme for a modern tech startup..."
          className="w-full p-2 border border-gray-300 dark:border-gray-600 rounded-md mb-3 h-24 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:ring-indigo-500 focus:border-indigo-500"
          rows="3"
        />
        {error && <p className="text-red-500 text-xs mb-3"><AlertTriangle size={14} className="inline mr-1"/>{error}</p>}
        <div className="flex justify-end space-x-3">
          <button
            onClick={onClose}
            className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-600 hover:bg-gray-200 dark:hover:bg-gray-500 rounded-md"
            disabled={isLoading}
          >
            Cancel
          </button>
          <button
            onClick={handleSubmit}
            className="px-4 py-2 text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 rounded-md flex items-center justify-center"
            disabled={isLoading}
          >
            {isLoading ? <Loader2 size={18} className="animate-spin mr-2" /> : <Sparkles size={16} className="mr-2" />}
            {isLoading ? 'Suggesting...' : 'Suggest Palette'}
          </button>
        </div>
      </div>
    </div>
  );
};


const ControlsSidebar = () => {
  const { activeThemeId, setActiveThemeId, currentSettings, updateSetting, updateMultipleSettings, resetCurrentTheme, availableThemes } = useTheme();
  const [isAIPaletteModalOpen, setIsAIPaletteModalOpen] = useState(false);

  const handleAISuggestion = (suggestedPalette) => {
    // Map API response to our settings structure if needed, or use directly if names match
    // For now, assuming direct match based on PALETTE_SUGGESTION_SCHEMA
    updateMultipleSettings(suggestedPalette);
  };


  return (
    <div className="w-full md:w-80 lg:w-96 bg-gray-50 dark:bg-gray-800 p-4 overflow-y-auto h-full shadow-lg">
      <div className="mb-4">
        <label htmlFor="theme-select" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Select Theme</label>
        <select
          id="theme-select"
          value={activeThemeId}
          onChange={(e) => setActiveThemeId(e.target.value)}
          className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
        >
          {Object.entries(availableThemes).map(([id, theme]) => (
            <option key={id} value={id}>{theme.name}</option>
          ))}
        </select>
      </div>

      {/* <button
        onClick={() => setIsAIPaletteModalOpen(true)}
        className="mb-4 w-full flex items-center justify-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-gradient-to-r from-purple-500 to-indigo-600 hover:from-purple-600 hover:to-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
      >
        <Sparkles size={16} className="mr-2" /> Suggest Palette with AI ✨
      </button> */}
      <AIPaletteModal 
        isOpen={isAIPaletteModalOpen} 
        onClose={() => setIsAIPaletteModalOpen(false)}
        onSuggest={handleAISuggestion}
      />


      {Object.entries(EDITABLE_PROPERTIES_CONFIG).map(([sectionTitle, properties], index) => (
        <AccordionSection key={sectionTitle} title={sectionTitle} defaultOpen={index === 0}>
          {Object.entries(properties).map(([key, config]) => {
            const value = currentSettings[key]; 
            if (config.type === 'color') {
              return <ColorInput key={key} label={config.label} value={value} onChange={updateSetting} propertyKey={key} />;
            }
            if (config.type === 'text') {
              return <TextInput key={key} label={config.label} value={value} onChange={updateSetting} propertyKey={key} />;
            }
            if (config.type === 'select') {
              return <SelectInput key={key} label={config.label} value={value} onChange={updateSetting} propertyKey={key} options={config.options} />;
            }
            return null;
          })}
        </AccordionSection>
      ))}
      
      <button
        onClick={resetCurrentTheme}
        className="mt-6 w-full flex items-center justify-center px-4 py-2 border border-gray-300 dark:border-gray-500 rounded-md shadow-sm text-sm font-medium text-gray-700 dark:text-gray-200 bg-white dark:bg-gray-600 hover:bg-gray-50 dark:hover:bg-gray-500 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
      >
        <RefreshCw size={16} className="mr-2" /> Reset Current Theme
      </button>
    </div>
  );
};

const PreviewPane = () => {
  const { currentSettings } = useTheme();
  const [iframeKey, setIframeKey] = useState(0); 

  const generatedCss = useMemo(() => generateCssFromSettings(currentSettings), [currentSettings]);

  const previewHtmlContent = `
    <!DOCTYPE html>
    <html lang="en">
    <head>
      <meta charset="UTF-8">
      <meta name="viewport" content="width=device-width, initial-scale=1.0">
      <title>Preview</title>
      <link href="https://fonts.googleapis.com/css2?family=Inter:wght@300;400;500;600;700&family=Montserrat:wght@300;400;500;600;700&family=Poppins:wght@300;400;500;600;700&family=Roboto:wght@300;400;500;600;700&family=Open+Sans:wght@300;400;500;600;700&family=Times+New+Roman&display=swap" rel="stylesheet">
      <style>
        /* KEF Oasis Web Font Family - Unified (all weights under one family) */
        @font-face {
          font-family: 'KEF Oasis Web';
          src: url('./KEFOasisWeb-Thin.woff') format('woff');
          font-weight: 100;
          font-style: normal;
          font-display: swap;
        }
        @font-face {
          font-family: 'KEF Oasis Web';
          src: url('./KEFOasisWeb-Light.woff') format('woff');
          font-weight: 300;
          font-style: normal;
          font-display: swap;
        }
        @font-face {
          font-family: 'KEF Oasis Web';
          src: url('./KEFOasisWeb-Regular.woff') format('woff');
          font-weight: 400;
          font-style: normal;
          font-display: swap;
        }
        @font-face {
          font-family: 'KEF Oasis Web';
          src: url('./KEFOasisWeb-Medium.woff') format('woff');
          font-weight: 500;
          font-style: normal;
          font-display: swap;
        }
        @font-face {
          font-family: 'KEF Oasis Web';
          src: url('./KEFOasisWeb-SemiBold.woff') format('woff');
          font-weight: 600;
          font-style: normal;
          font-display: swap;
        }
        @font-face {
          font-family: 'KEF Oasis Web';
          src: url('./KEFOasisWeb-Bold.woff') format('woff');
          font-weight: 700;
          font-style: normal;
          font-display: swap;
        }
        @font-face {
          font-family: 'KEF Oasis Web Variable';
          src: url('./KEFOasisWeb-Variable.woff') format('woff');
          font-weight: 100 900;
          font-style: normal;
          font-display: swap;
        }
        
        /* KEF Oasis Web Font Family - Individual Weight Families */
        @font-face {
          font-family: 'KEF Oasis Web Thin';
          src: url('./KEFOasisWeb-Thin.woff') format('woff');
          font-weight: 100;
          font-style: normal;
          font-display: swap;
        }
        @font-face {
          font-family: 'KEF Oasis Web Light';
          src: url('./KEFOasisWeb-Light.woff') format('woff');
          font-weight: 300;
          font-style: normal;
          font-display: swap;
        }
        @font-face {
          font-family: 'KEF Oasis Web Regular';
          src: url('./KEFOasisWeb-Regular.woff') format('woff');
          font-weight: 400;
          font-style: normal;
          font-display: swap;
        }
        @font-face {
          font-family: 'KEF Oasis Web Medium';
          src: url('./KEFOasisWeb-Medium.woff') format('woff');
          font-weight: 500;
          font-style: normal;
          font-display: swap;
        }
        @font-face {
          font-family: 'KEF Oasis Web SemiBold';
          src: url('./KEFOasisWeb-SemiBold.woff') format('woff');
          font-weight: 600;
          font-style: normal;
          font-display: swap;
        }
        @font-face {
          font-family: 'KEF Oasis Web Bold';
          src: url('./KEFOasisWeb-Bold.woff') format('woff');
          font-weight: 700;
          font-style: normal;
          font-display: swap;
        }
      </style>
      <style id="dynamicThemeStyles">
        ${generatedCss}
      </style>
      <style>
        body { margin: 0; display: flex; min-height: 100vh; font-size: 14px; line-height: 1.5; }
        #kt_app_sidebar { width: 250px; flex-shrink: 0; display: flex; flex-direction: column; overflow: hidden; }
        #kt_app_sidebar_logo { padding: 20px; text-align: center; font-weight: bold; border-bottom: 1px solid var(--sidebar-border, #eee); }
        #kt_app_sidebar_menu_wrapper { padding: 15px; flex-grow: 1; overflow: hidden; }
        .menu-item { margin-bottom: 5px; position: relative; }
        .menu-title-header { font-size: 0.9rem; opacity: 0.7; margin-top: 10px; margin-bottom: 5px; padding: 0 15px; }
        .app-main-content-wrapper { flex-grow: 1; display: flex; flex-direction: column; overflow: hidden; }
        .lucide { display: inline-block; width: 1em; height: 1em; stroke-width: 2; vertical-align: middle; }
        /* Prevent navigation links from opening pages in iframe */
        a { pointer-events: none; cursor: default; }
      </style>
    </head>
    <body class="DataCentralPreviewContainer">
      <div id="kt_app_sidebar">
        <div id="kt_app_sidebar_logo">DataCentral</div>
        <div id="kt_app_sidebar_menu_wrapper">
          <div class="menu-item"><a href="#" class="menu-link active flex items-center"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" class="lucide lucide-layout-dashboard mr-2 w-4 h-4"><rect width="18" height="18" x="3" y="3" rx="2"></rect><path d="M3 9h18"></path><path d="M9 21V9"></path></svg><span class="menu-title">Dashboard</span></a></div>
          <div class="menu-item"><a href="#" class="menu-link flex items-center"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" class="lucide lucide-bar-chart-3 mr-2 w-4 h-4"><path d="M3 3v18h18"></path><path d="M18 17V9"></path><path d="M13 17V5"></path><path d="M8 17v-3"></path></svg><span class="menu-title">Analytics</span></a></div>
          <div class="menu-title-header">Management</div>
          <div class="menu-item"><a href="#" class="menu-link flex items-center"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" class="lucide lucide-users mr-2 w-4 h-4"><path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2"></path><circle cx="9" cy="7" r="4"></circle><path d="M22 21v-2a4 4 0 0 0-3-3.87"></path><path d="M16 3.13a4 4 0 0 1 0 7.75"></path></svg><span class="menu-title">Users</span></a></div>
          <div class="menu-item"><a href="#" class="menu-link flex items-center"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" class="lucide lucide-settings mr-2 w-4 h-4"><path d="M12.22 2h-.44a2 2 0 0 0-2 2v.18a2 2 0 0 1-1 1.73l-.43.25a2 2 0 0 1-2 0l-.15-.08a2 2 0 0 0-2.73.73l-.22.38a2 2 0 0 0 .73 2.73l.15.1a2 2 0 0 1 1 1.72v.51a2 2 0 0 1-1 1.74l-.15.09a2 2 0 0 0-.73 2.73l.22.38a2 2 0 0 0 2.73.73l.15-.08a2 2 0 0 1 2 0l.43.25a2 2 0 0 1 1 1.73V20a2 2 0 0 0 2 2h.44a2 2 0 0 0 2-2v-.18a2 2 0 0 1 1-1.73l.43-.25a2 2 0 0 1 2 0l.15.08a2 2 0 0 0 2.73-.73l.22-.38a2 2 0 0 0-.73-2.73l-.15-.1a2 2 0 0 1-1-1.72v-.51a2 2 0 0 1 1-1.74l.15-.09a2 2 0 0 0 .73-2.73l-.22-.38a2 2 0 0 0-2.73-.73l-.15.08a2 2 0 0 1-2 0l-.43-.25a2 2 0 0 1-1-1.73V4a2 2 0 0 0-2-2z"></path><circle cx="12" cy="12" r="3"></circle></svg><span class="menu-title">Settings</span></a></div>
        </div>
      </div>
      <div class="app-main-content-wrapper">
        <div id="kt_app_header_container">
          <div id="kt_app_header_wrapper">
            <span style="font-size: 1.125rem; font-weight: 600;">Page Title</span>
            <div>
              <button class="btn-header mr-2"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" class="lucide lucide-bell w-5 h-5"><path d="M6 8a6 6 0 0 1 12 0c0 7 3 9 3 9H3s3-2 3-9"></path><path d="M10.3 21a1.94 1.94 0 0 0 3.4 0"></path></svg></button>
              <button class="btn-header"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" class="lucide lucide-user-circle w-5 h-5"><circle cx="12" cy="12" r="10"></circle><circle cx="12" cy="10" r="3"></circle><path d="M7 20.662V19a2 2 0 0 1 2-2h6a2 2 0 0 1 2 2v1.662"></path></svg></button>
            </div>
          </div>
        </div>
        <div id="kt_app_page">
          <div class="card-body">
            <h2 style="font-size: 1.25rem; font-weight: 600; margin-bottom: 1rem;">Data Overview</h2>
            <div style="display: grid; grid-template-columns: repeat(auto-fit, minmax(200px, 1fr)); gap: 1rem; margin-bottom: 1.5rem;">
              <input type="text" class="form-control" placeholder="Search for data..." />
              <input type="date" class="form-control" />
            </div>
            <button class="btn-primary"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" class="lucide lucide-plus-circle w-4 h-4"><circle cx="12" cy="12" r="10"></circle><path d="M8 12h8"></path><path d="M12 8v8"></path></svg>Add New Record</button>
            <button class="btn-primary ml-2" disabled style="margin-left: 0.5rem;"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" class="lucide lucide-upload-cloud w-4 h-4"><path d="M4 14.899A7 7 0 1 1 15.71 8h1.79a4.5 4.5 0 0 1 2.5 8.242"></path><path d="M12 12v9"></path><path d="m16 16-4-4-4 4"></path></svg>Upload (Disabled)</button>
          </div>
          <div class="card-body primeng-datatable-container">
            <h2 style="font-size: 1.25rem; font-weight: 600; margin-bottom: 1rem; padding: 1rem;">User List</h2>
            <table class="p-datatable">
              <thead class="p-datatable-thead">
                <tr><th>ID</th><th>Name</th><th>Email</th><th>Role</th></tr>
              </thead>
              <tbody class="p-datatable-tbody">
                <tr><td>1</td><td>Alice Wonderland</td><td>alice@example.com</td><td>Admin</td></tr>
                <tr><td>2</td><td>Bob The Builder</td><td>bob@example.com</td><td>Editor</td></tr>
                <tr><td>3</td><td>Charlie Brown</td><td>charlie@example.com</td><td>Viewer</td></tr>
              </tbody>
            </table>
            <div class="p-paginator">
              <span>Showing 1-3 of 3 items</span>
              <div>
                <button class="mr-1" disabled style="margin-right: 0.25rem;">&lt;</button>
                <button>&gt;</button>
              </div>
            </div>
          </div>
        </div>
        <div id="kt_app_footer">
          &copy; ${new Date().getFullYear()} DataCentral. All rights reserved.
        </div>
      </div>
    </body>
    </html>
  `;

  useEffect(() => {
    setIframeKey(prev => prev + 1);
  }, [currentSettings.fontFamily, currentSettings.pageBg]);


  return (
    <div className="flex-1 bg-gray-200 dark:bg-gray-900 p-0 md:p-6 flex items-center justify-center">
      <iframe
        key={iframeKey}
        srcDoc={previewHtmlContent}
        title="Theme Preview"
        className="w-full h-full bg-white shadow-2xl rounded-lg border-none"
        sandbox="allow-scripts allow-same-origin" 
      />
    </div>
  );
};

const AppHeader = () => {
  const { currentSettings, availableThemes, activeThemeId } = useTheme();
  
  const handleExportCss = () => {
    const themeName = availableThemes[activeThemeId]?.name || 'Customized Theme';
    const settingsWithName = { ...currentSettings, name: themeName };
    const cssContent = generateCssFromSettings(settingsWithName);
    const blob = new Blob([cssContent], { type: 'text/css;charset=utf-8' });
    saveAs(blob, 'datacentral-theme.css');
  };

  return (
    <header className="bg-white dark:bg-gray-800 shadow-md p-4 flex justify-between items-center sticky top-0 z-10">
      <div className="flex items-center">
        <Palette size={28} className="text-indigo-600 dark:text-indigo-400 mr-3" />
        <h1 className="text-xl font-semibold text-gray-800 dark:text-gray-100">DataCentral Theme Customizer</h1>
      </div>
      <button
        onClick={handleExportCss}
        className="px-4 py-2 bg-indigo-600 hover:bg-indigo-700 text-white text-sm font-medium rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 flex items-center"
      >
        <Download size={16} className="mr-2" /> Export CSS
      </button>
    </header>
  );
}

function ThemeCustomizer() {
  return (
    <ThemeProvider>
      <div className={`flex flex-col h-screen antialiased text-gray-900 bg-gray-100 dark:bg-gray-900 dark:text-gray-100 transition-colors duration-300`}>
        <AppHeader />
        <div className="flex flex-1 overflow-hidden">
          <ControlsSidebar />
          <PreviewPane />
        </div>
      </div>
    </ThemeProvider>
  );
}

export default ThemeCustomizer;
